import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../../../Constants';

const coverageOptions = [
  'Covered Under Contract',
  'Filling Fees Only',
  'Test Only',
  'Test and Filing Fees',
  'Combine Test and Filing Fees',
];

function DeviceInspections({
  inspections = [],
  onInspectionsChange,
  buildingSite = '',
  agencyId = '',
  deviceId = '',
}) {
  // isExpanded = false => two columns (2 records/row)
  // isExpanded = true => one column (1 record/row)
  const [isExpanded, setIsExpanded] = useState(false);

  const [localInspections, setLocalInspections] = useState([]);

  useEffect(() => {
    // If you only want “Active” inspections
    const active = inspections.filter((insp) => insp.status === 'Active');
    const init = active.map((insp) => ({ ...insp, isEditing: false }));
    setLocalInspections(init);
  }, [inspections]);

  const handleChange = (idx, fieldName, value) => {
    setLocalInspections((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [fieldName]: value };
      return updated;
    });
    if (onInspectionsChange) {
      onInspectionsChange(
        localInspections.map((insp, i) =>
          i === idx ? { ...insp, [fieldName]: value } : insp
        )
      );
    }
  };

  const handleCheckbox = (idx, fieldName, checked) => {
    setLocalInspections((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [fieldName]: checked };

      // If unchecking, clear coverage/cost fields
      if (fieldName === 'performTest' && !checked) {
        updated[idx].performCoverage = '';
        updated[idx].performCost = '';
      }
      if (fieldName === 'witnessTest' && !checked) {
        updated[idx].witnessCoverage = '';
        updated[idx].witnessHours = '';
        updated[idx].witnessCost = '';
      }
      return updated;
    });
    if (onInspectionsChange) {
      onInspectionsChange(
        localInspections.map((insp, i) => {
          if (i === idx) {
            const newItem = { ...insp, [fieldName]: checked };
            if (fieldName === 'performTest' && !checked) {
              newItem.performCoverage = '';
              newItem.performCost = '';
            }
            if (fieldName === 'witnessTest' && !checked) {
              newItem.witnessCoverage = '';
              newItem.witnessHours = '';
              newItem.witnessCost = '';
            }
            return newItem;
          }
          return insp;
        })
      );
    }
  };

  const handleEditClick = (idx) => {
    setLocalInspections((prev) => {
      const updated = [...prev];
      updated[idx].isEditing = true;
      return updated;
    });
  };

  const handleSaveClick = async (idx) => {
    const insp = localInspections[idx];
    if (!insp.inspectionId) {
      alert('Cannot save. Missing inspectionId.');
      return;
    }

    const payload = { ...insp };
    delete payload.isEditing;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/inspections/${insp.inspectionId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        throw new Error(
          `Failed to update inspection. Status: ${response.status}`
        );
      }

      setLocalInspections((prev) => {
        const updated = [...prev];
        updated[idx].isEditing = false;
        return updated;
      });

      if (onInspectionsChange) {
        onInspectionsChange(localInspections);
      }

      alert('Saved changes to the inspection!');
    } catch (err) {
      console.error('Error saving inspection:', err);
      alert('Failed to save. Check console for details.');
    }
  };

  // Decide the container’s columns based on isExpanded
  // isExpanded = true => single column (1 rec/row)
  // isExpanded = false => two columns (2 recs/row)
  const getGridClasses = () => {
    return isExpanded
      ? 'grid grid-cols-1 gap-4'
      : 'grid grid-cols-1 md:grid-cols-2 gap-4';
  };

  return (
    <div className="mt-10 h-1 border border-gray-300 rounded-lg w-full">
      <div className="bg-gray-200 p-3 flex justify-between items-center">
        <h3 className="font-semibold">
          Active Device Inspections
          {buildingSite && (
            <span className="ml-2 text-sm text-gray-600">
              (Building: {buildingSite})
            </span>
          )}
        </h3>
        {/* Toggle Button */}
        <button
          className="px-3 py-1 text-sm bg-blue-50 border border-blue-200 rounded hover:bg-blue-100"
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          {isExpanded ? '2 per Row' : '1 per Row'}
        </button>
      </div>

      <div className="p-4">
        {localInspections.length === 0 ? (
          <p className="text-gray-600">No active inspections found.</p>
        ) : (
          // We pick 1 or 2 columns
          <div className={getGridClasses()}>
            {localInspections.map((insp, index) => {
              const isEditing = insp.isEditing || false;

              return (
                <div
                  key={insp.inspectionId || index}
                  className="bg-white p-4 rounded-md border border-gray-300 shadow-sm space-y-3"
                >
                  {/* Fields */}
                  <div className="flex items-center">
                    <label className="w-1/3 text-sm font-medium mr-2">
                      Inspection ID
                    </label>
                    <input
                      type="text"
                      disabled
                      className="w-2/3 border border-gray-300 rounded-md p-2 bg-gray-100"
                      value={insp.inspectionId || ''}
                      onChange={() => {}}
                    />
                  </div>

                  <div className="flex items-center">
                    <label className="w-1/3 text-sm font-medium mr-2">
                      Status
                    </label>
                    <input
                      type="text"
                      disabled
                      className="w-2/3 border border-gray-300 rounded-md p-2 bg-gray-100"
                      value={insp.status || ''}
                      onChange={() => {}}
                    />
                  </div>

                  <div className="flex items-center">
                    <label className="w-1/3 text-sm font-medium mr-2">
                      Test
                    </label>
                    <input
                      type="text"
                      disabled={!isEditing}
                      className="w-2/3 border border-gray-300 rounded-md p-2"
                      value={insp.test || ''}
                      onChange={(e) =>
                        handleChange(index, 'test', e.target.value)
                      }
                    />
                  </div>

                  <div className="flex items-center">
                    <label className="w-1/3 text-sm font-medium mr-2">
                      Approval
                    </label>
                    <input
                      type="date"
                      disabled={!isEditing}
                      className="w-2/3 border border-gray-300 rounded-md p-2"
                      value={insp.approvalDate?.substring(0, 10) || ''}
                      onChange={(e) =>
                        handleChange(index, 'approvalDate', e.target.value)
                      }
                    />
                  </div>

                  <div className="flex items-center">
                    <label className="w-1/3 text-sm font-medium mr-2">
                      Next
                    </label>
                    <input
                      type="date"
                      disabled={!isEditing}
                      className="w-2/3 border border-gray-300 rounded-md p-2"
                      value={insp.nextDate?.substring(0, 10) || ''}
                      onChange={(e) =>
                        handleChange(index, 'nextDate', e.target.value)
                      }
                    />
                  </div>

                  {/* Perform Test */}
                  <div className="flex items-center">
                    <label className="w-1/3 text-sm font-medium mr-2">
                      Perform?
                    </label>
                    <input
                      type="checkbox"
                      disabled={!isEditing}
                      className="h-5 w-5"
                      checked={!!insp.performTest}
                      onChange={(e) =>
                        handleCheckbox(index, 'performTest', e.target.checked)
                      }
                    />
                  </div>

                  <div className="flex items-center">
                    <label className="w-1/3 text-sm font-medium mr-2">
                      Perf Cov
                    </label>
                    <select
                      disabled={!isEditing}
                      className="w-2/3 border border-gray-300 rounded-md p-2"
                      value={insp.performCoverage || ''}
                      onChange={(e) =>
                        handleChange(index, 'performCoverage', e.target.value)
                      }
                    >
                      <option value="">-- none --</option>
                      {coverageOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center">
                    <label className="w-1/3 text-sm font-medium mr-2">
                      Perf Cost
                    </label>
                    <input
                      type="number"
                      disabled={!isEditing}
                      className="w-2/3 border border-gray-300 rounded-md p-2"
                      value={insp.performCost || ''}
                      onChange={(e) =>
                        handleChange(index, 'performCost', e.target.value)
                      }
                    />
                  </div>

                  {/* Witness Test */}
                  <div className="flex items-center">
                    <label className="w-1/3 text-sm font-medium mr-2">
                      Witness?
                    </label>
                    <input
                      type="checkbox"
                      disabled={!isEditing}
                      className="h-5 w-5"
                      checked={!!insp.witnessTest}
                      onChange={(e) =>
                        handleCheckbox(index, 'witnessTest', e.target.checked)
                      }
                    />
                  </div>

                  <div className="flex items-center">
                    <label className="w-1/3 text-sm font-medium mr-2">
                      Wit Cov
                    </label>
                    <select
                      disabled={!isEditing}
                      className="w-2/3 border border-gray-300 rounded-md p-2"
                      value={insp.witnessCoverage || ''}
                      onChange={(e) =>
                        handleChange(index, 'witnessCoverage', e.target.value)
                      }
                    >
                      <option value="">-- none --</option>
                      {coverageOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center">
                    <label className="w-1/3 text-sm font-medium mr-2">
                      Wit Hrs
                    </label>
                    <input
                      type="number"
                      disabled={!isEditing}
                      className="w-2/3 border border-gray-300 rounded-md p-2"
                      value={insp.witnessHours || ''}
                      onChange={(e) =>
                        handleChange(index, 'witnessHours', e.target.value)
                      }
                    />
                  </div>

                  <div className="flex items-center">
                    <label className="w-1/3 text-sm font-medium mr-2">
                      Wit Cost
                    </label>
                    <input
                      type="number"
                      disabled={!isEditing}
                      className="w-2/3 border border-gray-300 rounded-md p-2"
                      value={insp.witnessCost || ''}
                      onChange={(e) =>
                        handleChange(index, 'witnessCost', e.target.value)
                      }
                    />
                  </div>

                  {/* Hours */}
                  <div className="flex items-center">
                    <label className="w-1/3 text-sm font-medium mr-2">
                      Hours
                    </label>
                    <input
                      type="number"
                      disabled={!isEditing}
                      className="w-2/3 border border-gray-300 rounded-md p-2"
                      value={insp.hours || ''}
                      onChange={(e) =>
                        handleChange(index, 'hours', e.target.value)
                      }
                    />
                  </div>

                  <div className="flex items-center">
                    <label className="w-1/3 text-sm font-medium mr-2">
                      Hrs Cost
                    </label>
                    <input
                      type="number"
                      disabled={!isEditing}
                      className="w-2/3 border border-gray-300 rounded-md p-2"
                      value={insp.hoursCost || ''}
                      onChange={(e) =>
                        handleChange(index, 'hoursCost', e.target.value)
                      }
                    />
                  </div>

                  {/* Action buttons */}
                  <div className="flex justify-end mt-3">
                    {!insp.isEditing ? (
                      <button
                        className="px-4 py-2 text-white bg-blue-500 hover:bg-blue-600 rounded-md"
                        onClick={() => handleEditClick(index)}
                      >
                        Edit
                      </button>
                    ) : (
                      <button
                        className="px-4 py-2 text-white bg-green-500 hover:bg-green-600 rounded-md"
                        onClick={() => handleSaveClick(index)}
                      >
                        Save
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default DeviceInspections;
